import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { FrameVariantBulkPreview } from "@/api/dragon-tiles";
import type { ChannelMedium, FramePreview } from "@/types/dragon-tiles";

export interface FrameFilters {
  search?: string;
  medium?: ChannelMedium;
  is_active?: boolean;
  order_by?: "create_date" | "modify_date" | "name" | "is_active";
  order?: "asc" | "desc";
}

import {
  dragonTileQueryOptions,
  framePreviewsPageQueryOptions,
} from "@/api/dragon-tiles";
import { listsArrayQueryOptions } from "@/api/listsFeatureV2";
import { FRAMES_PAGE_SIZE } from "@/components/TilesPagination/TilesPagination";
import { getParentPoolInnerTile } from "@/features/DragonTiles/innerTileUtils";
import { parentTileDisabledHash } from "@/features/DragonTiles/parentTileDisabledHash";
import { shouldHideNewSubscriberListOptIn } from "@/features/DragonTiles/shouldHideNewSubscriberListOptIn";

export interface FramePreviewCard {
  frame: FramePreview;
  variantPreviews: Record<string, FrameVariantBulkPreview>;
}

export const buildFramePreviewsFilters = (
  parentTile: string,
  parentPoolInnerTileId: string | undefined,
  filters?: FrameFilters,
) => ({
  dragon_tile: parentTile,
  ...(parentPoolInnerTileId ? { inner_tile: parentPoolInnerTileId } : {}),
  ...(filters?.search ? { search: filters.search } : {}),
  ...(filters?.medium ? { medium: filters.medium } : {}),
  ...(filters?.is_active !== undefined ? { is_active: filters.is_active } : {}),
  order_by: filters?.order_by ?? "modify_date",
  order: filters?.order ?? "desc",
});

export function useDragonTileWithFrames(
  siteId: string,
  parentTile: string,
  page = 1,
  pageSize = FRAMES_PAGE_SIZE,
  filters?: FrameFilters,
) {
  const { data: dragonTile } = useSuspenseQuery(
    dragonTileQueryOptions(siteId, parentTile),
  );

  const parentPoolInnerTileId = getParentPoolInnerTile(dragonTile)?.id;

  const { data: framesResponse, isFetching: isFramesFetching } = useQuery({
    ...framePreviewsPageQueryOptions(
      siteId,
      page,
      pageSize,
      buildFramePreviewsFilters(parentTile, parentPoolInnerTileId, filters),
    ),
    placeholderData: (prev) => prev,
  });

  const totalCount = framesResponse?.count ?? 0;

  const frames: FramePreviewCard[] = useMemo(
    () =>
      (framesResponse?.results ?? []).map((frame) => ({
        frame,
        variantPreviews: {},
      })),
    [framesResponse],
  );

  const hasInclusionLists = !!dragonTile?.config?.inclusions;
  const hasExclusionLists = !!dragonTile?.config?.exclusions;
  const isNoConfigTile =
    dragonTile &&
    parentTileDisabledHash[dragonTile.slug]?.disableConfig === true;
  const hideNewSubscriberListOptIn =
    dragonTile && shouldHideNewSubscriberListOptIn(dragonTile.slug);
  const shouldFetchLists =
    !isNoConfigTile &&
    !hideNewSubscriberListOptIn &&
    (hasInclusionLists || hasExclusionLists);

  const { data: lists = [] } = useQuery({
    ...listsArrayQueryOptions(siteId),
    enabled: shouldFetchLists,
  });

  return {
    dragonTile,
    frames,
    lists,
    totalCount,
    isFramesFetching,
  };
}
